import {
  Button,
  Group,
  type MantineTheme,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { debtorAtom } from 'app/Private/Clients/Detail/client-atom';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { useProfile } from 'init/use-enforce-profile';
import { useAtomValue } from 'jotai';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import MoveRecoveryPlanInvoices from 'shared/action-component/MoveRecoveryPlanInvoices';
import AYSModal from 'shared/components';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';
import { useUpdateAccountManager } from 'shared/hooks/use-update-account-manager';
import {
  type StepsSwitch,
  useUpdateClientPreferences,
} from 'shared/hooks/use-update-client-preferences';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';
import { DialogShowId, dialogHide, showDialog } from 'store/view/view.actions';
import { SendingMediumManager } from './SendingMediumManager';
import { usePreferenceForm } from './usePreferenceForm';

const selectStyles = (theme: MantineTheme) => ({
  input: {
    textAlign: 'right' as const,
    color: theme.colors.gray[10],
  },
});

export const PreferencePanel = () => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const profile = useProfile();
  const { debtor, refetch } = useAtomValue(debtorAtom);

  const { updateClientPreferences, isUpdateClientPreferencesLoading } =
    useUpdateClientPreferences();
  const { updateAccountManager } = useUpdateAccountManager();
  const { users } = useLoadLightUsers();

  const [isMediumModalOpened, { open: openMediumModal, close: closeMediumModal }] =
    useDisclosure(false);

  const {
    form,
    arePreferencesLoading,
    recoveryPlans,
    invoiceTemplates,
    creditNoteTemplates,
    company,
    handleError,
  } = usePreferenceForm(debtor);

  const handleFormSubmit = () => {
    updateClientPreferences({ id: String(debtor.id), data: form.values });

    const accountManagerId = form.values.accountManagerId
      ? Number(form.values.accountManagerId)
      : null;

    if (form.isDirty('accountManagerId')) {
      updateAccountManager(
        { id: String(debtor.id), data: { accountManagerId } },
        { onSuccess: refetch },
      );
    }

    form.resetDirty();
  };

  const setDefaultRecoveryPlan = (planId: string, moveInvoices: boolean) => {
    if (!moveInvoices) {
      form.setValues({ recoveryPlanId: planId });
      return;
    }

    MoveRecoveryPlanInvoices({
      title: t(i18nKeys.PLAN.INVOICES),
      newPlanId: planId,
      oldPlanId: debtor.recovery_plan ? debtor.recovery_plan.id : company.recovery_plan?.id,
      debtor_id: debtor.id,
      onSubmit: (steps_switch: StepsSwitch) => {
        form.setValues({ recoveryPlanId: planId, stepsSwitch: steps_switch });
        dialogHide(DialogShowId.CUSTOM);
      },
    });
  };

  const askMoveInvoicesWithPlan = (planId: string | null) => {
    if (!planId) return;
    if (!debtor.has_invoices_to_switch) {
      setDefaultRecoveryPlan(planId, false);
      return;
    }
    showDialog({
      id: DialogShowId.CONFIRM,
      title: t(i18nKeys.ATTENTION),
      children: (
        <AYSModal
          text={t(i18nKeys.CLIENT.MOVE_INVOICES_IN_PLAN)}
          cancelButtonText={t(i18nKeys.NO)}
          confirmButtonText={t(i18nKeys.YES)}
          onCancel={() => setDefaultRecoveryPlan(planId, false)}
          onConfirm={() => setDefaultRecoveryPlan(planId, true)}
        />
      ),
    });
  };

  return (
    <>
      <Panel
        withBorder
        isLoading={arePreferencesLoading}
        title={t(i18nKeys.CLIENT.TABS.PREFERENCES)}
        topRight={
          <SaveButton
            form="clientPreferenceForm"
            type="submit"
            disabled={!form.isDirty()}
            size="compact-sm"
            loading={isUpdateClientPreferencesLoading}
          />
        }
      >
        <form onSubmit={form.onSubmit(handleFormSubmit, handleError)} id="clientPreferenceForm">
          <Stack>
            <Group justify="space-between">
              <Text>{t(i18nKeys.ACCOUNT_MANAGER)}</Text>
              <Tooltip
                label={t(i18nKeys.FORM.PREFERENCES.TOOLTIP.ONLY_ADMIN_CAN_CHANGE_ACCOUNT_MANAGER)}
                disabled={profile.isAdmin}
              >
                <Select
                  {...form.getInputProps('accountManagerId')}
                  data={users?.map(user => ({
                    label: user.name.full,
                    value: String(user.id),
                  }))}
                  w={150}
                  searchable
                  styles={selectStyles(theme)}
                  variant="unstyled"
                  placeholder={t(i18nKeys.NOT_ASSIGNED)}
                  allowDeselect
                  disabled={!profile.isAdmin || !users}
                />
              </Tooltip>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text>{t(i18nKeys.FORM.PREFERENCES.SEND_TO_ALL_EMAILS)}</Text>
              <Select
                {...form.getInputProps('sendToAllEmails')}
                checkIconPosition="right"
                styles={selectStyles(theme)}
                allowDeselect={false}
                variant="unstyled"
                data={[
                  {
                    label: `${t(i18nKeys.DEFAULT)} (${company.send_to_all_emails ? t(i18nKeys.YES) : t(i18nKeys.NO)})`,
                    value: 'default',
                  },
                  { label: t(i18nKeys.YES), value: 'true' },
                  { label: t(i18nKeys.NO), value: 'false' },
                ]}
              />
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text>{t(i18nKeys.FORM.PREFERENCES.SEND_TO_CONTACT_PERSON_AND_DEBTOR_EMAIL)}</Text>
              <Select
                {...form.getInputProps('sendToContactPersonAndDebtorEmail')}
                checkIconPosition="right"
                styles={selectStyles(theme)}
                allowDeselect={false}
                variant="unstyled"
                miw={160}
                data={[
                  {
                    label: `${t(i18nKeys.DEFAULT)} (${company.send_to_contact_person_and_debtor_email ? t(i18nKeys.YES) : t(i18nKeys.NO)})`,
                    value: 'default',
                  },
                  { label: t(i18nKeys.YES), value: 'true' },
                  { label: t(i18nKeys.NO), value: 'false' },
                ]}
              />
            </Group>
            <Group justify="space-between">
              <Text>{t(i18nKeys.RECOVERY_PLAN)}</Text>
              <Select
                {...form.getInputProps('recoveryPlanId')}
                checkIconPosition="right"
                w={200}
                styles={selectStyles(theme)}
                allowDeselect={false}
                data={
                  recoveryPlans?.map(plan => ({
                    label:
                      plan.id === company.recovery_plan?.id
                        ? `${plan.name} (${t(i18nKeys.DEFAULT).toLowerCase()})`
                        : plan.name,
                    value: plan.id,
                  })) || []
                }
                onChange={value => askMoveInvoicesWithPlan(value)}
                variant="unstyled"
              />
            </Group>
            <Group justify="space-between">
              <Text>{t(i18nKeys.FORM.PREFERENCES.PAYMENT_PLAN_ACCESS)}</Text>
              <Select
                {...form.getInputProps('allowPaymentPlans')}
                checkIconPosition="right"
                w={250}
                styles={selectStyles(theme)}
                allowDeselect={false}
                variant="unstyled"
                data={[
                  {
                    label: `${t(i18nKeys.DEFAULT)} (${company.allow_payment_plans ? t(i18nKeys.FORM.PREFERENCES.ALLOWED) : t(i18nKeys.FORM.PREFERENCES.NOT_ALLOWED)})`,
                    value: 'default',
                  },
                  { label: t(i18nKeys.FORM.PREFERENCES.ALLOWED), value: 'true' },
                  { label: t(i18nKeys.FORM.PREFERENCES.FORBIDDEN), value: 'false' },
                ]}
              />
            </Group>
            <Group justify="space-between">
              <Text>{t(i18nKeys.FORM.PREFERENCES.INVOICE_TEMPLATE)}</Text>
              <Select
                {...form.getInputProps('defaultInvoiceTemplateId')}
                checkIconPosition="right"
                w={250}
                styles={selectStyles(theme)}
                allowDeselect={false}
                variant="unstyled"
                data={invoiceTemplates?.map(template => ({
                  label:
                    Number(template.id) === company.default_templates.invoice.id
                      ? `${template.name} (${t(i18nKeys.DEFAULT).toLowerCase()})`
                      : template.name,
                  value: template.id,
                }))}
              />
            </Group>
            <Group justify="space-between">
              <Text>{t(i18nKeys.FORM.PREFERENCES.CREDIT_NOTE_TEMPLATE)}</Text>
              <Select
                {...form.getInputProps('defaultCreditNoteTemplateId')}
                checkIconPosition="right"
                w={250}
                styles={selectStyles(theme)}
                allowDeselect={false}
                variant="unstyled"
                data={creditNoteTemplates?.map(template => ({
                  label:
                    Number(template.id) === company.default_templates.credit_note.id
                      ? `${template.name} (${t(i18nKeys.DEFAULT).toLowerCase()})`
                      : template.name,
                  value: template.id,
                }))}
              />
            </Group>
            <Group justify="space-between">
              <Text>{t(i18nKeys.DELAY_BETWEEN_REMINDERS)}</Text>
              <NumberInput
                {...form.getInputProps('daysBetweenReminder')}
                styles={selectStyles(theme)}
                hideControls
                suffix={t(i18nKeys.FORM.PREFERENCES.DAYS_SUFFIX)}
                allowNegative={false}
                allowDecimal={false}
                min={0}
                placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.ENTER_VALUE)}
                variant="unstyled"
              />
            </Group>
            <Group justify="space-between">
              <Text>{t(i18nKeys.FORM.PREFERENCES.MINIMUM_BALANCE)}</Text>
              <NumberInput
                {...form.getInputProps('minimumSaldoSending')}
                styles={selectStyles(theme)}
                hideControls
                suffix={` ${getCurrencySymbolFromCurrencyCode(debtor.currency)}`}
                allowNegative={false}
                allowDecimal={false}
                min={0}
                placeholder={t(i18nKeys.FORM.PREFERENCES.ENTER_VALUE_IN_CURRENCY, {
                  currency: getCurrencySymbolFromCurrencyCode(debtor.currency),
                })}
                variant="unstyled"
              />
            </Group>
            <Group justify="space-between">
              <Text>{t(i18nKeys.DEBTOR.SENDING_MEDIUMS)}</Text>
              <Button onClick={openMediumModal} variant="outline" size="compact-sm">
                {t(i18nKeys.EDIT)}
              </Button>
            </Group>
          </Stack>
        </form>
      </Panel>
      <Modal
        title={t(i18nKeys.DEBTOR.SENDING_MEDIUMS)}
        opened={isMediumModalOpened}
        onClose={closeMediumModal}
        size="xl"
      >
        <SendingMediumManager company={company} form={form} />
        <Group justify="end">
          <Button
            disabled={!form.isDirty('sendingMedium')}
            onClick={() => {
              handleFormSubmit();
              closeMediumModal();
            }}
          >
            {t(i18nKeys.SAVE)}
          </Button>
        </Group>
      </Modal>
    </>
  );
};
